import { JSX, useContext, useEffect, useState } from "react";
import {
  Navigate,
  Route,
  Routes,
  useLocation,
  useNavigate,
} from "react-router-dom";
import { PageTemplate } from "../shared/layout/PageTemplate";
import Dashboard from "./dashboard";
import { AuthContext } from "../context/AuthContext";
import { useTranslation } from "react-i18next"; // Import useTranslation
import Login from "../authentication/login/Login";

import UserList from "./user/List";
import AdminForm from "./user/AdminForm";
import JobTypeList from "./jobType/List";
import JobTypeForm from "./jobType/Form";
import JobCategoriesList from "./jobCategories/List";

import JobTargetGroupsList from "./jobTargetGroups/List";
import JobTargetGroupsForm from "./jobTargetGroups/Form";
import CompanyList from "./company/List";
import SettingsForm from "./settings/Form";
import BlogForm from "./blog/Form";
import BlogList from "./blog/List";
import AccountProfile from "./user/profile";
import LocationList from "./location/List";
import LocationForm from "./location/Form";
import PageList from "./page/List";
import PageForm from "./page/Form";
import QaCategoryList from "./qaCategories/List";
import QaCategoryForm from "./qaCategories/Form";
import QaForm from "./qa/Form";
import QaList from "./qa/List";
import SectionForm from "./sections/Form";
import SectionList from "./sections/List";
import JobList from "./job/List";
import JobForm from "./job/Form";

import User from "./user/Index";
import OfferList from "./offers/List";
import OfferForm from "./offers/Form";
import OfferCoachDocumentList from "./offerCoachDocuments/List";
import QualificationsList from "./qualifications/List";
import QualificationsForm from "./qualifications/Form";
import PackagesForm from "./packages/Form";
import PackagesList from "./packages/List";
import JobFormatList from "./jobFormat/List";
import JobFormatForm from "./jobFormat/Form";
import DeclineReasonList from "./declineReason/List";
import DeclineReasonForm from "./declineReason/Form";
import FreeQualificationsList from "./freeQualifications/List";
import Company from "./company/Index";
import RecommendedCategoriesList from "./jobRecommendedCategories/List";
import StatisticsList from "./statistics/List";
import PartnerList from "./partner/List";
import PartnerForm from "./partner/Form";
import PartnerPageSettingsForm from "./partner-page-settings/Form";
import EmailList from "./email/List";
import EmailView from "./email/View";
import FeedbackList from "./feedback/List";
import FeedbackDetail from "./feedback/Detail";
import AuditLogList from "./auditLog/List";

export function BaseRoutes() {
  const { loading } = useContext(AuthContext);
  const [pageTitle, setPageTitle] = useState<string>("/");
  const { pathname } = useLocation();
  const { t } = useTranslation(); // Initialize translation hook
  const navigate = useNavigate();
  useEffect(() => {
    const token = localStorage.getItem("token");

    if (!token) {
      // Redirect to login if no token is present
      navigate("/", { replace: true });
    } else if (token && pathname === "/") {
      // Redirect to dashboard if token exists but the route is "/"
      navigate("/dashboard", { replace: true });
    }
  }, [pathname, navigate]);
  useEffect(() => {
    switch (true) {
      case pathname === "/":
        return setPageTitle(t("pageTitles.login"));

      case pathname === "/dashboard":
        return setPageTitle(t("pageTitles.dashboard"));
      case pathname === "/profile-overview":
      case pathname === "/personal-info":
      case pathname === "/change-password":
        return setPageTitle(t("pageTitles.accountProfile"));
      case pathname === "/company":
        return setPageTitle(t("pageTitles.company"));
      case pathname === "/company/create":
        return setPageTitle(t("pageTitles.createCompany"));
      case pathname.includes("/company/edit"):
        return setPageTitle(t("pageTitles.editCompany"));
      case pathname === "/users":
        return setPageTitle(t("pageTitles.users"));
      case pathname === "/users/create":
        return setPageTitle(t("pageTitles.createUser"));
      case pathname.includes("/users/edit"):
        return setPageTitle(t("pageTitles.editUser"));
      case pathname === "/admins/create":
        return setPageTitle(t("pageTitles.createSuperadmin"));
      case pathname.includes("/admins/edit"):
        return setPageTitle(t("pageTitles.editSuperadmin"));
      case pathname === "/settings":
        return setPageTitle(t("pageTitles.settings"));
      case pathname === "/jobs":
        return setPageTitle(t("pageTitles.jobs"));
      case pathname === "/jobs/create":
        return setPageTitle(t("pageTitles.createJob"));
      case pathname.includes("/jobs/edit"):
        return setPageTitle(t("pageTitles.editJob"));
      case pathname === "/job-types":
        return setPageTitle(t("pageTitles.jobTypes"));
      case pathname === "/job-types/create":
        return setPageTitle(t("pageTitles.createJobType"));
      case pathname.includes("/job-types/edit"):
        return setPageTitle(t("pageTitles.editJobType"));
      case pathname === "/job-categories":
        return setPageTitle(t("pageTitles.jobCategories"));
      case pathname === "/job-categories/create":
        return setPageTitle(t("pageTitles.createJobCategory"));
      case pathname.includes("/job-categories/edit"):
        return setPageTitle(t("pageTitles.editJobCategory"));
      case pathname === "/job-target-groups":
        return setPageTitle(t("pageTitles.jobTargetGroups"));
      case pathname === "/job-target-groups/create":
        return setPageTitle(t("pageTitles.createJobTargetGroup"));
      case pathname.includes("/job-target-groups/edit"):
        return setPageTitle(t("pageTitles.editJobTargetGroup"));
      case pathname.includes("/recommended-categories"):
        return setPageTitle("Recommended job categories");
      case pathname === "/blogs":
        return setPageTitle(t("pageTitles.blogs"));
      case pathname === "/blogs/create":
        return setPageTitle(t("pageTitles.createBlog"));
      case pathname.includes("/blogs/edit"):
        return setPageTitle(t("pageTitles.editBlog"));
      case pathname === "/locations":
        return setPageTitle(t("pageTitles.locations"));
      case pathname === "/locations/create":
        return setPageTitle(t("pageTitles.createLocation"));
      case pathname.includes("/locations/edit"):
        return setPageTitle(t("pageTitles.editLocation"));
      case pathname.includes("/locations"):
        return setPageTitle(t("pageTitles.locations")); // For dynamic levels
      case pathname === "/pages":
        return setPageTitle(t("pageTitles.pages"));
      case pathname === "/pages/create":
        return setPageTitle(t("pageTitles.createPage"));
      case pathname.includes("/pages/edit"):
        return setPageTitle(t("pageTitles.editPage"));
      case pathname === "/qa-categories":
        return setPageTitle(t("pageTitles.qaCategories"));
      case pathname === "/qa-categories/create":
        return setPageTitle(t("pageTitles.createQaCategory"));
      case pathname.includes("/qa-categories/edit"):
        return setPageTitle(t("pageTitles.editQaCategory"));
      case pathname === "/qas":
        return setPageTitle(t("pageTitles.qas"));
      case pathname === "/qas/create":
        return setPageTitle(t("pageTitles.createQa"));
      case pathname.includes("/qas/edit"):
        return setPageTitle(t("pageTitles.editQa"));
      case pathname === "/sections":
        return setPageTitle(t("pageTitles.sections"));
      case pathname === "/sections/create":
        return setPageTitle(t("pageTitles.createSection"));
      case pathname.includes("/sections.edit"):
        return setPageTitle(t("pageTitles.editSection"));
      case pathname === "/packages":
        return setPageTitle(t("pageTitles.packages")); // Renamed to Packages
      case pathname === "/packages/create":
        return setPageTitle(t("pageTitles.createPackage")); // Renamed to Packages
      case pathname.includes("/packages/edit"):
        return setPageTitle(t("pageTitles.editPackage")); // Renamed to Packages
      case pathname === "/offers":
        return setPageTitle(t("pageTitles.offers"));
      case pathname === "/offer-coach-documents":
        return setPageTitle(t("pageTitles.offerCoachDocuments"));
      case pathname === "/offers/create":
        return setPageTitle(t("pageTitles.createOffer"));
      case pathname.includes("/offers/edit"):
        return setPageTitle(t("pageTitles.editOffer"));
      case pathname === "/regions":
        return setPageTitle(t("pageTitles.regions"));
      case pathname === "/regions.create":
        return setPageTitle(t("pageTitles.createRegion"));
      case pathname.includes("/regions/edit"):
        return setPageTitle(t("pageTitles.editRegion"));
      case pathname === "/qualifications":
        return setPageTitle(t("pageTitles.qualifications"));
      case pathname === "/qualifications/create":
        return setPageTitle(t("pageTitles.createQualification"));
      case pathname.includes("/qualifications/edit"):
        return setPageTitle(t("pageTitles.editQualification"));
      case pathname === "/job-formats": // Added jobFormat
        return setPageTitle(t("pageTitles.jobFormats"));
      case pathname === "/job-formats/create": // Added jobFormat
        return setPageTitle(t("pageTitles.createJobFormat"));
      case pathname.includes("/job-formats/edit"): // Added jobFormat
        return setPageTitle(t("pageTitles.editJobFormat"));
      case pathname === "/decline-reasons": // Added declineReason
        return setPageTitle(t("pageTitles.declineReasons"));
      case pathname === "/decline-reasons/create": // Added declineReason
        return setPageTitle(t("pageTitles.createDeclineReason"));
      case pathname.includes("/decline-reasons/edit"): // Added declineReason
        return setPageTitle(t("pageTitles.editDeclineReason"));
      case pathname.includes("/free-qualifications"): // Added declineReason
        return setPageTitle(t("pageTitles.freeQualifications"));
      case pathname.includes("/statistics"):
        return setPageTitle(t("pageTitles.statistics"));
      case pathname === "/partners":
        return setPageTitle(t("pageTitles.partners"));
      case pathname === "/partners/create":
        return setPageTitle(t("pageTitles.createPartner"));
      case pathname.includes("/partners/edit"):
        return setPageTitle(t("pageTitles.editPartner"));
      case pathname === "/partner-page-settings":
        return setPageTitle(t("pageTitles.partnerPageSettings"));
      case pathname === "/emails":
        return setPageTitle(t("pageTitles.emails"));
      case pathname.includes("/emails/view"):
        return setPageTitle(t("pageTitles.viewEmail"));
      case pathname === "/feedbacks":
        return setPageTitle(t("pageTitles.feedbacks", "Feedback"));
      case pathname.startsWith("/feedbacks/"):
        return setPageTitle(t("pageTitles.feedbackDetail", "Feedback Detail"));
      case pathname === "/audit-logs":
        return setPageTitle(t("pageTitles.auditLogs"));
      default:
        setPageTitle(t("pageTitles.invalid"));
    }
  }, [pathname, t]);

  const shouldHideSidebar = !localStorage.getItem("token");

  if (loading) {
    return <div>{t("loading")}</div>;
  }

  const ProtectedRoute = ({ element }: { element: JSX.Element }) => {
    const token = localStorage.getItem("token");
    return token ? element : <Navigate to="/" replace />;
  };

  const LoginRoute = () => {
    return localStorage.getItem("token") ? (
      <Navigate to="/dashboard" />
    ) : (
      <Login />
    );
  };

  return (
    <>
      {shouldHideSidebar ? (
        <Routes>
          <Route path="/" element={<LoginRoute />} />
          <Route path="*" element={<Navigate to="/" replace />} />{" "}
          {/* Catch-all route */}
        </Routes>
      ) : (
        <PageTemplate title={pageTitle}>
          <Routes>
            <Route
              path="/dashboard"
              element={<ProtectedRoute element={<Dashboard />} />}
            />
            <Route
              path="/profile-overview"
              element={<ProtectedRoute element={<AccountProfile />} />}
            />
            <Route
              path="/personal-info"
              element={<ProtectedRoute element={<AccountProfile />} />}
            />
            <Route
              path="/change-password"
              element={<ProtectedRoute element={<AccountProfile />} />}
            />
            <Route
              path="/company"
              element={<ProtectedRoute element={<CompanyList />} />}
            />
            <Route
              path="/company/create"
              element={<ProtectedRoute element={<Company />} />}
            />
            <Route
              path="/company/edit/:id"
              element={<ProtectedRoute element={<Company />} />}
            />
            <Route
              path="/users"
              element={<ProtectedRoute element={<UserList />} />}
            />
            <Route
              path="/users/create"
              element={<ProtectedRoute element={<User />} />}
            />
            <Route
              path="/users/edit/:id"
              element={<ProtectedRoute element={<User />} />}
            />
            <Route
              path="/admins/create"
              element={<ProtectedRoute element={<AdminForm />} />}
            />
            <Route
              path="/admins/edit/:id"
              element={<ProtectedRoute element={<AdminForm />} />}
            />
            <Route
              path="/settings"
              element={<ProtectedRoute element={<SettingsForm />} />}
            />
            <Route
              path="/jobs"
              element={<ProtectedRoute element={<JobList />} />}
            />
            <Route
              path="/jobs/create"
              element={<ProtectedRoute element={<JobForm />} />}
            />
            <Route
              path="/jobs/edit/:id"
              element={<ProtectedRoute element={<JobForm />} />}
            />
            <Route
              path="/job-types"
              element={<ProtectedRoute element={<JobTypeList />} />}
            />
            <Route
              path="/job-types/create"
              element={<ProtectedRoute element={<JobTypeForm />} />}
            />
            <Route
              path="/job-types/edit/:id"
              element={<ProtectedRoute element={<JobTypeForm />} />}
            />
            <Route
              path="/job-categories"
              element={<ProtectedRoute element={<JobCategoriesList />} />}
            />
            <Route
              path="/job-target-groups"
              element={<ProtectedRoute element={<JobTargetGroupsList />} />}
            />
            <Route
              path="/job-target-groups/create"
              element={<ProtectedRoute element={<JobTargetGroupsForm />} />}
            />
            <Route
              path="/job-target-groups/edit/:id"
              element={<ProtectedRoute element={<JobTargetGroupsForm />} />}
            />
            <Route
              path="/recommended-categories"
              element={
                <ProtectedRoute element={<RecommendedCategoriesList />} />
              }
            />
            <Route
              path="/blogs"
              element={<ProtectedRoute element={<BlogList />} />}
            />
            <Route
              path="/blogs/create"
              element={<ProtectedRoute element={<BlogForm />} />}
            />
            <Route
              path="/blogs/edit/:id"
              element={<ProtectedRoute element={<BlogForm />} />}
            />
            <Route
              path="/locations"
              element={<ProtectedRoute element={<LocationList />} />}
            />
            <Route
              path="/locations/:parentId"
              element={<ProtectedRoute element={<LocationList />} />} // Dynamic route for child levels
            />
            <Route
              path="/locations/create"
              element={<ProtectedRoute element={<LocationForm />} />}
            />
            <Route
              path="/locations/create/:parentId"
              element={<ProtectedRoute element={<LocationForm />} />}
            />
            <Route
              path="/locations/edit/:id"
              element={<ProtectedRoute element={<LocationForm />} />}
            />
            <Route
              path="/pages"
              element={<ProtectedRoute element={<PageList />} />}
            />
            <Route
              path="/pages/create"
              element={<ProtectedRoute element={<PageForm />} />}
            />
            <Route
              path="/pages/edit/:id"
              element={<ProtectedRoute element={<PageForm />} />}
            />
            <Route
              path="/qa-categories"
              element={<ProtectedRoute element={<QaCategoryList />} />}
            />
            <Route
              path="/qa-categories/create"
              element={<ProtectedRoute element={<QaCategoryForm />} />}
            />
            <Route
              path="/qa-categories/edit/:id"
              element={<ProtectedRoute element={<QaCategoryForm />} />}
            />
            <Route
              path="/qas"
              element={<ProtectedRoute element={<QaList />} />}
            />
            <Route
              path="/qas/create"
              element={<ProtectedRoute element={<QaForm />} />}
            />
            <Route
              path="/qas/edit/:id"
              element={<ProtectedRoute element={<QaForm />} />}
            />
            <Route
              path="/sections"
              element={<ProtectedRoute element={<SectionList />} />}
            />
            <Route
              path="/sections/create"
              element={<ProtectedRoute element={<SectionForm />} />}
            />
            <Route
              path="/sections/edit/:id"
              element={<ProtectedRoute element={<SectionForm />} />}
            />
            <Route
              path="/layouts"
              element={<ProtectedRoute element={<Dashboard />} />}
            />
            <Route
              path="/layout1"
              element={<ProtectedRoute element={<Dashboard />} />}
            />
            <Route
              path="/packages"
              element={<ProtectedRoute element={<PackagesList />} />}
            />
            <Route
              path="/packages/create"
              element={<ProtectedRoute element={<PackagesForm />} />}
            />
            <Route
              path="/packages/edit/:id"
              element={<ProtectedRoute element={<PackagesForm />} />}
            />
            <Route
              path="/offers"
              element={<ProtectedRoute element={<OfferList />} />}
            />
            <Route
              path="/offers/create"
              element={<ProtectedRoute element={<OfferForm />} />}
            />
            <Route
              path="/offers/edit/:id"
              element={<ProtectedRoute element={<OfferForm />} />}
            />
            <Route
              path="/offer-coach-documents"
              element={<ProtectedRoute element={<OfferCoachDocumentList />} />}
            />
            {/* <Route
              path="/regions"
              element={<ProtectedRoute element={<RegionList />} />}
            />
            <Route
              path="/regions/create"
              element={<ProtectedRoute element={<RegionForm />} />}
            />
            <Route
              path="/regions/edit/:id"
              element={<ProtectedRoute element={<RegionForm />} />}
            /> */}
            <Route
              path="/qualifications"
              element={<ProtectedRoute element={<QualificationsList />} />}
            />
            <Route
              path="/qualifications/create"
              element={<ProtectedRoute element={<QualificationsForm />} />}
            />
            <Route
              path="/qualifications/edit/:id"
              element={<ProtectedRoute element={<QualificationsForm />} />}
            />
            <Route
              path="/job-formats"
              element={<ProtectedRoute element={<JobFormatList />} />}
            />
            <Route
              path="/job-formats/create"
              element={<ProtectedRoute element={<JobFormatForm />} />}
            />
            <Route
              path="/job-formats/edit/:id"
              element={<ProtectedRoute element={<JobFormatForm />} />}
            />
            <Route
              path="/decline-reasons"
              element={<ProtectedRoute element={<DeclineReasonList />} />}
            />
            <Route
              path="/decline-reasons/create"
              element={<ProtectedRoute element={<DeclineReasonForm />} />}
            />
            <Route
              path="/decline-reasons/edit/:id"
              element={<ProtectedRoute element={<DeclineReasonForm />} />}
            />
            <Route
              path="/free-qualifications"
              element={<ProtectedRoute element={<FreeQualificationsList />} />}
            />
            <Route
              path="/statistics"
              element={<ProtectedRoute element={<StatisticsList />} />}
            />
            <Route
              path="/partners"
              element={<ProtectedRoute element={<PartnerList />} />}
            />
            <Route
              path="/partners/create"
              element={<ProtectedRoute element={<PartnerForm />} />}
            />
            <Route
              path="/partners/edit/:id"
              element={<ProtectedRoute element={<PartnerForm />} />}
            />
            <Route
              path="/partner-page-settings"
              element={
                <ProtectedRoute element={<PartnerPageSettingsForm />} />
              }
            />
            <Route
              path="/emails"
              element={<ProtectedRoute element={<EmailList />} />}
            />
            <Route
              path="/emails/view/:id"
              element={<ProtectedRoute element={<EmailView />} />}
            />
            <Route
              path="/feedbacks"
              element={<ProtectedRoute element={<FeedbackList />} />}
            />
            <Route
              path="/feedbacks/:id"
              element={<ProtectedRoute element={<FeedbackDetail />} />}
            />
            <Route
              path="/audit-logs"
              element={<ProtectedRoute element={<AuditLogList />} />}
            />
            <Route
              path="*"
              element={
                localStorage.getItem("token") ? (
                  <Navigate to="/dashboard" replace />
                ) : (
                  <Navigate to="/" replace />
                )
              }
            />{" "}
            {/* Catch-all route */}
          </Routes>
        </PageTemplate>
      )}
    </>
  );
}
